import { DoctorLayout } from "@/components/layout/doctor-layout";
import { useState, useEffect } from "react";
import { ShieldCheck, CheckCircle2, Circle, ChevronDown, ChevronUp, Calendar, Info } from "lucide-react";
import { Link } from "wouter";

const GMC_REQUIREMENTS = [
  { key: "cpd", label: "CPD", desc: "Continuing professional development — minimum 50 hours/year" },
  { key: "qip", label: "Quality Improvement Activity (QIP)", desc: "Clinical audit, service evaluation, or improvement project" },
  { key: "significant_event", label: "Significant Event Analysis", desc: "Analysis of a significant event, including near-misses and complaints" },
  { key: "colleague_feedback", label: "Colleague Feedback", desc: "Multi-source feedback (MSF) from colleagues across roles" },
  { key: "patient_feedback", label: "Patient Feedback", desc: "Structured feedback from patients or carers" },
  { key: "complaints_review", label: "Review of Complaints & Compliments", desc: "Reflection on any complaints received and responses made" },
];

const BMA_GUIDANCE = [
  {
    title: "BMA CPD Documentation",
    content: `The BMA advises doctors to keep a CPD diary or log and document their CPD activities throughout the revalidation cycle. You should be able to explain how each activity has improved your clinical practice or professional development. Documentation should include the date, title, category, credits, and a brief reflection on learning outcomes.`,
  },
  {
    title: "Job Planning (PAs & SPAs)",
    content: `Your job plan should include time for Supporting Professional Activities (SPAs). The BMA recommends a minimum of 1.5 SPAs (equivalent to approximately 1.5 sessions per week) for a full-time consultant, as agreed with your employer. SPAs cover CPD, audit, teaching, research, clinical governance, and appraisal preparation. Ensure your SPA allocation is formally documented in your job plan and reviewed annually.`,
  },
  {
    title: "Annual Appraisal Preparation",
    content: `GMC revalidation is based on 5 annual appraisals. Each appraisal should cover all supporting information types across the 5-year cycle. You should reflect on CPD activities, QIPs, significant events, feedback, and complaints with your appraiser. Evidence should be collated in advance and discussed to demonstrate your fitness to practise.`,
  },
  {
    title: "BMA Resources",
    content: `The BMA provides a CPD toolkit, appraisal guidance, and job planning templates. Visit bma.org.uk/cpd for the CPD toolkit, job planning advice, and revalidation resources. The BMA also provides a consultant job planning guide and SPA negotiation toolkit available to members.`,
  },
];

const LS_CHECKLIST_KEY = "rheumhub_doctor_gmc_checklist";
const LS_APPRAISAL_KEY = "rheumhub_doctor_appraisal_date";
const LS_REVALIDATION_KEY = "rheumhub_doctor_revalidation_date";

function loadChecklist(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(LS_CHECKLIST_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
}

function saveChecklist(val: Record<string, boolean>) {
  localStorage.setItem(LS_CHECKLIST_KEY, JSON.stringify(val));
}

export default function RevalidationDashboard() {
  const [checklist, setChecklist] = useState<Record<string, boolean>>(loadChecklist);
  const [expandedGuidance, setExpandedGuidance] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ total: number; clinical: number; educational: number; professional: number; year: number } | null>(null);

  const appraisalDate = localStorage.getItem(LS_APPRAISAL_KEY);
  const revalidationDate = localStorage.getItem(LS_REVALIDATION_KEY);

  useEffect(() => {
    fetch("/api/doctor/cpd/summary")
      .then((r) => r.json())
      .then((d) => setSummary(d))
      .catch(() => {});
  }, []);

  const toggleItem = (key: string) => {
    const next = { ...checklist, [key]: !checklist[key] };
    setChecklist(next);
    saveChecklist(next);
  };

  const checkedCount = Object.values(checklist).filter(Boolean).length;
  const totalItems = GMC_REQUIREMENTS.length;

  const getDaysUntil = (dateStr: string | null) => {
    if (!dateStr) return null;
    const diff = new Date(dateStr).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  const appraisalDays = getDaysUntil(appraisalDate);
  const revalidationDays = getDaysUntil(revalidationDate);

  const GMC_ANNUAL_TARGET = 50;
  const cpdPct = summary ? Math.min((summary.total / GMC_ANNUAL_TARGET) * 100, 100) : 0;

  return (
    <DoctorLayout title="Revalidation Dashboard">
      <div className="space-y-6">

        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-900 to-indigo-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck className="h-8 w-8 text-indigo-300" />
            <div>
              <h2 className="text-xl font-bold">GMC Revalidation Tracker</h2>
              <p className="text-indigo-300 text-sm">5-year revalidation cycle · GMC Supporting Information requirements</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/10 rounded-xl p-4">
              {appraisalDays !== null ? (
                <>
                  <p className={`text-2xl font-bold ${appraisalDays < 30 ? "text-red-300" : appraisalDays < 90 ? "text-amber-300" : "text-white"}`}>
                    {appraisalDays > 0 ? `${appraisalDays}d` : "Due"}
                  </p>
                  <p className="text-xs text-indigo-300">Until next appraisal</p>
                  <p className="text-xs text-indigo-400 mt-0.5">{new Date(appraisalDate!).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-slate-400">—</p>
                  <p className="text-xs text-indigo-300">Next appraisal</p>
                  <Link href="/doctor/settings" className="text-xs text-indigo-400 hover:text-indigo-200 underline">Set date in settings</Link>
                </>
              )}
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              {revalidationDays !== null ? (
                <>
                  <p className={`text-2xl font-bold ${revalidationDays < 180 ? "text-amber-300" : "text-white"}`}>
                    {revalidationDays > 0 ? `${Math.ceil(revalidationDays / 30)}mo` : "Due"}
                  </p>
                  <p className="text-xs text-indigo-300">Until revalidation</p>
                  <p className="text-xs text-indigo-400 mt-0.5">{new Date(revalidationDate!).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-bold text-slate-400">—</p>
                  <p className="text-xs text-indigo-300">Revalidation date</p>
                  <Link href="/doctor/settings" className="text-xs text-indigo-400 hover:text-indigo-200 underline">Set date in settings</Link>
                </>
              )}
            </div>
            <div className="bg-white/10 rounded-xl p-4">
              <p className="text-2xl font-bold">{summary ? `${summary.total.toFixed(0)}` : "—"}</p>
              <p className="text-xs text-indigo-300">CPD hrs this year</p>
              <div className="mt-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                <div className="h-full bg-indigo-400 rounded-full transition-all" style={{ width: `${cpdPct}%` }} />
              </div>
            </div>
          </div>

          {/* CPD category breakdown */}
          {summary && (
            <div className="grid grid-cols-3 gap-3 mt-2">
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-lg font-bold">{summary.clinical.toFixed(1)}</p>
                <p className="text-xs text-indigo-300">Cat 1 · Clinical</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-lg font-bold">{summary.educational.toFixed(1)}</p>
                <p className="text-xs text-indigo-300">Cat 2 · Educational</p>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <p className="text-lg font-bold">{summary.professional.toFixed(1)}</p>
                <p className="text-xs text-indigo-300">Cat 3 · Professional</p>
              </div>
            </div>
          )}
        </div>

        {/* Supporting Information Checklist */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-slate-900">GMC Supporting Information Checklist</h3>
            <span className="text-sm font-medium text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200">
              {checkedCount}/{totalItems} complete
            </span>
          </div>
          <p className="text-sm text-slate-500">Tick each item as you gather it for your appraisal folder. This checklist is saved locally to your browser.</p>

          <div className="space-y-2">
            {GMC_REQUIREMENTS.map((req) => {
              const checked = !!checklist[req.key];
              return (
                <button
                  key={req.key}
                  onClick={() => toggleItem(req.key)}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                    checked
                      ? "bg-emerald-50 border-emerald-200"
                      : "bg-slate-50 border-slate-200 hover:border-indigo-200 hover:bg-indigo-50/30"
                  }`}
                >
                  {checked
                    ? <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
                    : <Circle className="h-5 w-5 text-slate-300 shrink-0 mt-0.5" />}
                  <div>
                    <p className={`text-sm font-semibold ${checked ? "text-emerald-800" : "text-slate-900"}`}>{req.label}</p>
                    <p className={`text-xs ${checked ? "text-emerald-600" : "text-slate-500"}`}>{req.desc}</p>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="w-full bg-slate-100 rounded-full h-2">
            <div
              className="bg-emerald-500 h-2 rounded-full transition-all"
              style={{ width: `${(checkedCount / totalItems) * 100}%` }}
            />
          </div>
        </div>

        {/* CPD Progress link */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="h-6 w-6 text-indigo-500" />
            <div>
              <p className="font-semibold text-slate-900 text-sm">CPD Activity Log</p>
              <p className="text-xs text-slate-500">Log and track your CPD hours against the 50-hour annual target</p>
            </div>
          </div>
          <Link href="/doctor/cpd">
            <button className="px-4 py-2 bg-indigo-700 text-white rounded-xl text-sm font-medium hover:bg-indigo-800 transition-colors">
              View Log
            </button>
          </Link>
        </div>

        {/* BMA Guidance Panel */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Info className="h-5 w-5 text-indigo-500" />
            <h3 className="font-bold text-slate-900">BMA CPD Guidance</h3>
          </div>
          <p className="text-xs text-slate-500">Reference information on BMA CPD documentation requirements, job planning, and appraisal preparation.</p>

          {BMA_GUIDANCE.map((g) => {
            const expanded = expandedGuidance === g.title;
            return (
              <div key={g.title} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedGuidance(expanded ? null : g.title)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-slate-900">{g.title}</span>
                  {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                </button>
                {expanded && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-slate-600 leading-relaxed">{g.content}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          This revalidation tracker is a personal planning tool only. It does not interface with GMC Online, SOAR, or any formal appraisal system. Verify all revalidation requirements directly with your Responsible Officer and the GMC (gmc-uk.org/revalidation).
        </div>

      </div>
    </DoctorLayout>
  );
}
